/* eslint-disable no-console */
import core from '@sbi/core';
import merge from 'lodash/merge';
import * as utils from './styleUtils';
import { DEFAULT_SETTINGS, SLF_SEL } from './constants';
import BrandStyleTemplate from './themeTemplates/BrandStyleTemplate';
import ButtonStyleTemplate from './themeTemplates/ButtonStyleTemplate';
import DashboardStyleTemplate from './themeTemplates/DashboardStyleTemplate';
import FontStyleTemplate from './themeTemplates/FontStyleTemplate';
import NavverStyleTemplate from './themeTemplates/NavverStyleTemplate';
import BackgroundStyleTemplate from './themeTemplates/BackgroundStyleTemplate';

const { convertStyleObjToCss } = utils;
const { general: { backgroundColor: defaultBgColor } } = DEFAULT_SETTINGS;

/**
 * @private
 */
function buildCssRulesForSettings(settings, options) {
    const templates = [
        BrandStyleTemplate(settings),
        FontStyleTemplate(settings),
        ButtonStyleTemplate(settings),
        DashboardStyleTemplate(settings),
        NavverStyleTemplate(settings),
        BackgroundStyleTemplate(settings)
    ];
    return templates.map((template) => convertStyleObjToCss(template, options))
        .join(' ');
}

export const getDefaultDesignSettings = (designSettings) => {
    return merge(
        {},
        DEFAULT_SETTINGS,
        {
            typography: {
                fontFamily: designSettings.typography.fontFamily,
            },
            general: {
                /* brand colors should be applied as we do not want to lose white-labeling */
                brandColor: designSettings.general.brandColor,
                primaryButtonBaseColor: designSettings.general.primaryButtonBaseColor,
                primaryButtonHoverColor: designSettings.general.primaryButtonHoverColor,
                primaryButtonTextColor: designSettings.general.primaryButtonTextColor,
            },
            /* keep widget design */
            dashboards: designSettings.dashboards,
        }
    );
};

class ThemeService extends core.events.EventTarget {
    constructor() {
        super();
        this.sheet = null;
        this.fontSheet = null;
        this.fontDiv = null;
        this.loadedFonts = [];
        this.dynamicStyleRules = [];
        this.applyDefaults();
    }

    /**
     * Set the element where div with styles should append.
     * document.body is used by default
     * @param {HTMLElement} htmlElement
     */
    setRootElement(htmlElement) {
        this.rootElement = htmlElement;
    }

    /**
     * @private
     */
    _ensureSheetIsCreated() {
        if (!this.sheet || !this.fontSheet) {
            const anotherInstanceStylesheet = document.getElementById('@sbi/styleguide');
            if (anotherInstanceStylesheet) {
                throw new Error(
                    'Another instance of @sbi/styleguide found! Not that @sbi/styleguide exist'
                    + ' on the global scope and should be external in your module. More about it'
                    + ' https://webpack.js.org/configuration/externals/'
                );
            }
            this.sheet = document.createElement('style');
            this.sheet.id = '@sbi/styleguide';
            this.getRootElement()
                .appendChild(this.sheet);
            this.fontSheet = document.createElement('style');
            this.fontDiv = document.createElement('div');
            this.fontDiv.style.zIndex = '-9999';
            this.fontDiv.style.top = '-9999px';
            this.fontDiv.style.position = 'absolute';
            this.getRootElement()
                .appendChild(this.fontSheet);
            this.getRootElement()
                .appendChild(this.fontDiv);
        }
    }

    /**
     * @private
     */
    _updateCssRules() {
        if (this.getRootElement()) {
            this._ensureSheetIsCreated();
            this.sheet.innerHTML = `${buildCssRulesForSettings(this.designSettings, {
                wrapper: true,
                wrapperClass: '.sis-scope',
            })} ${buildCssRulesForSettings(DEFAULT_SETTINGS, {
                wrapper: true,
                wrapperClass: `.sis-scope ${SLF_SEL.DEFAULT}`,
            })} ${this.getDynamicStyles(this.designSettings)}`;
        }
    }

    /**
     * @private
     */
    _addSingleStyleRule(rule) {
        if (this.getRootElement()) {
            this._ensureSheetIsCreated();
            this.sheet.innerHTML += this.buildSingleModuleStyles(rule, this.designSettings);
        }
    }

    /**
     * Mark font as loaded so it won't be loaded again upon "loadFont" method call
     * @param font fontName
     */
    markFontAsLoaded(font) {
        if (!this.loadedFonts.includes(font)) {
            this.loadedFonts.push(font);
        }
    }

    /**
     * Load font by name. Add @font-face into the DOM
     * If the font is already loaded by this method, it won't be loaded again
     * @param font
     */
    loadFont(font, path = '/resources/base/fonts', customFont = false) {
        const proxyUrl = this.getRootEventTarget().getProxyURI
            ? this.getRootEventTarget()
                .getProxyURI()
            : this.getProxyURI();
        this._ensureSheetIsCreated();
        if (!this.loadedFonts.includes(font)) {
            const fontFaceRules = `
@font-face {
    font-family: "${font}";
    font-weight: normal;
    font-style: normal;
    src: url('${proxyUrl}${path}/${font}-Regular.eot');
    src: local('${font}-Regular'),
    url('${proxyUrl}${path}/${font}-Regular.eot?#iefix') format('embedded-opentype'),
    url('${proxyUrl}${path}/${font}-Regular.ttf') format('truetype');
}
@font-face {
    font-family: "${font}";
    font-weight: 600;
    font-style: normal;
    src: url('${proxyUrl}${path}/${font}-SemiBold.eot');
    src: local('${font}-SemiBold'),
    url('${proxyUrl}${path}/${font}-SemiBold.eot?#iefix') format('embedded-opentype'),
    url('${proxyUrl}${path}/${font}-SemiBold.ttf') format('truetype');
}
@font-face {
    font-family: "${font}";
    font-weight: bold;
    font-style: normal;
    src: url('${proxyUrl}${path}/${font}-Bold.eot');
    src: local('${font}-Bold'),
    url('${proxyUrl}${path}/${font}-Bold.eot?#iefix') format('embedded-opentype'),
    url('${proxyUrl}${path}/${font}-Bold.ttf') format('truetype');
}`;
            const markup = `
<div style="font-family:'${font}', sans-serif; font-weight: normal">a</div>
<div style="font-family:'${font}', sans-serif; font-weight: bold">a</div>
<div style="font-family:'${font}', sans-serif; font-weight: 600">a</div> `;

            if (customFont) {
                this.fontSheet.insertAdjacentHTML('afterend', `<style>${fontFaceRules}</style>`);
                this.fontDiv.insertAdjacentHTML(
                    'afterend',
                    `<div style="z-index: -9999; top: -9999px; position: absolute">${markup}</div>`
                );
            } else {
                this.fontSheet.innerHTML += fontFaceRules;
                this.fontDiv.insertAdjacentHTML('beforeend', markup);
            }
            this.markFontAsLoaded(font);
        }
    }

    /**
     * Update designSettings object and apply changes
     * Create list of css rules based on design settings and insert them into the DOM
     * @param designSettings - may be partial
     */
    updateDesignSettings(designSettings) {
        this.designSettings = { ...(this.designSettings || {}), ...designSettings };
        this.designSettings.typography = {
            ...this.designSettings.typography,
            fontFamily: this.designSettings.customFontSelected && this.designSettings.customFont
                ? this.designSettings.typography.customFont
                : this.designSettings.typography.predefinedFont,
        };
        const font = this.designSettings.typography.fontFamily;

        this._updateCssRules();

        this.loadFont(font);
    }

    /**
     * Apply default settings. Ignore fonts loading.
     */
    applyDefaults() {
        this.designSettings = DEFAULT_SETTINGS;
        this.designSettings.typography = {
            ...this.designSettings.typography,
            fontFamily: this.designSettings.customFontSelected && this.designSettings.customFont
                ? this.designSettings.typography.customFont
                : this.designSettings.typography.predefinedFont,
        };
        if (this.getRootElement()) {
            this._updateCssRules();
        } else {
            window.addEventListener('DOMContentLoaded', () => {
                this._updateCssRules();
            });
        }
    }

    /**
     * @method dynamic style rules
     * @param designSettings
     * @return {string} - css rules based on design settings
     */
    getDynamicStyles(designSettings) {
        return this.dynamicStyleRules.reduce((acc, obj) => {
            const moduleStyle = this.buildSingleModuleStyles(obj, designSettings);
            acc.push(moduleStyle);
            return acc;
        }, [])
            .join(' ');
    }

    // eslint-disable-next-line class-methods-use-this
    buildSingleModuleStyles(moduleRules, designSettings) {
        // Keep the selected font-family even for default colored parts of application
        const defaultSettings = getDefaultDesignSettings(designSettings);
        const { getModuleStyle, conf = {} } = moduleRules;
        const confForDefaults = {
            ...conf,
            wrapper: true,
            wrapperClass: `${conf.wrapperClass || ''} ${SLF_SEL.DEFAULT}`,
        };
        let defaultModuleStyleWithWrapper = '';
        if (conf.wrapperClass) {
            const confForDefaultsWithWrapper = {
                ...conf,
                wrapper: true,
                wrapperClass: `${conf.wrapperClass || ''}${SLF_SEL.DEFAULT}`,
            };
            defaultModuleStyleWithWrapper = convertStyleObjToCss(
                getModuleStyle(defaultSettings),
                confForDefaultsWithWrapper
            );
        }

        if (conf.debug) {
            console.log('----Get module style obj start:----');
            console.log(getModuleStyle(designSettings));
            console.log('----Get module style obj end:----');
        }
        const moduleStyle = convertStyleObjToCss(getModuleStyle(designSettings), conf);
        const defaultModuleStyle = convertStyleObjToCss(
            getModuleStyle(defaultSettings),
            confForDefaults
        );
        if (conf.debug) {
            // eslint-disable-next-line no-console
            console.log('----Generated css start:----');
            console.log(`${defaultModuleStyleWithWrapper}\n${defaultModuleStyle}\n${moduleStyle}`);
            console.log('-----Generated css end------');
        }
        return (
            `${moduleStyle}${defaultModuleStyleWithWrapper}\n${defaultModuleStyle}\n`
        );
    }

    /**
     * @method to inject styles of individual module/component into custom dynamic styles
     * @param getModuleStyle - function which return css rules (in js format) based on design
     * settings
     * @param conf - injection configuration
     */
    injectStyleRules = (getModuleStyle, conf) => {
        this.dynamicStyleRules.push({
            getModuleStyle,
            conf,
        });
        this._addSingleStyleRule({
            getModuleStyle,
            conf,
        });
    };

    /**
     * @method same as injectStyleRules, but add wrapper: true, so all your jss code will be wrapped
     * into 1 more selector
     * @param getModuleStyle
     * @param conf
     */
    injectStylesWithWrapper = (getModuleStyle, conf = {}) => {
        conf.wrapper = true;
        conf.wrapperClass = '.sis-scope';
        return this.injectStyleRules(getModuleStyle, conf);
    };

    /**
     * @method Injects a style rule so that the provided CSS selector and CSS property
     * use the current theme brand color.
     * @param {string} cssSelector CSS selector to apply brand color to.
     * @param {string} cssProperty CSS property to apply brand color to.
     */
    injectBrandColor = (cssSelector, cssProperty) => {
        const getModuleStyle = (designSettings) => {
            const { brandColor } = designSettings.general;
            return {
                [cssSelector]: {
                    [cssProperty]: brandColor,
                },
            };
        };
        this.injectStyleRules(getModuleStyle);
    };

    /**
     * @method Injects a style rule so that the provided CSS selector has either:
     * 1. a slightly different background than the current theme background color OR
     * 2. the default background color
     * @param {string} cssSelector CSS selector to apply background color to.
     */
    injectSlightlyDifferentBackgroundColor = (cssSelector) => {
        const getModuleStyle = (designSettings) => {
            const { backgroundColor } = designSettings.general;
            return {
                [cssSelector]: {
                    backgroundColor: this.colorOrDefaultForBack(
                        utils.makeASlightlyDifferentColor(backgroundColor),
                        DEFAULT_SETTINGS.general.backgroundColor
                    ),
                },
            };
        };
        this.injectStyleRules(getModuleStyle);
    };

    getDesignSettings = () => {
        return this.designSettings;
    };

    /**
     * Get default proxy url. Used as a prefix to network requests.
     * If this component has another rootEventTarget, it's getProxyUrl function will be used instead
     * @return {String} proxyUrl
     */
    getProxyURI = () => {
        return this.defaultProxyUrl;
    };

    /**
     * Set default proxy url. Used as a prefix to network requests.
     * If this component has another rootEventTarget, it's getProxyUrl function will be used instead
     * @param {String} url
     */
    setProxyURI = (url) => {
        this.defaultProxyUrl = url;
    };

    getRootElement() {
        return this.rootElement || document.body;
    }

    /**
     * This function encapsulates the "if background color is default" condition
     * @param color
     * @param defaultColor
     * @return {*}
     */
    colorOrDefaultForBack = (color, defaultColor) => {
        if (this.designSettings.general.backgroundColor === defaultBgColor) {
            return defaultColor;
        } else {
            return color;
        }
    };
}

export default ThemeService;
